// src/pages/VacationBalance/VacationBalanceList.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HistoryIcon from '@mui/icons-material/History';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import WorkHistoryRoundedIcon from '@mui/icons-material/WorkHistoryRounded';

import api from '../../services/api';
import {
  ConfirmDialog,
  DataTableShell,
  EmptyState,
  FilterBar,
  PageHeader,
  PageLayout,
  StatCard,
} from '../../components/ui';

// Annual balance at or below this many days is flagged as "needs attention".
const LOW_THRESHOLD = 5;

// -------------------------------------------------------------------------
// Pure helpers
// -------------------------------------------------------------------------
const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const deptOf = (user) => (user?.department_name || '').trim();
const managerOf = (user) => (user?.manager?.name || '').trim();

function getInitials(fullName = '') {
  const words = String(fullName).trim().split(' ').filter(Boolean);
  const initials = words.map((word) => word[0]).join('').toUpperCase();
  return initials.substring(0, 2) || 'U?';
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function formatDays(value) {
  const amount = num(value);
  return `${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} day${amount === 1 ? '' : 's'}`;
}

// Recover the full picture from the three stored numbers. Entitlement (the
// original grant) is conserved as available + on_hold + days_taken because the
// accounting only ever moves days between those three buckets.
function computeBreakdown(balance) {
  const available = num(balance?.balance);
  const pending = num(balance?.on_hold);
  const used = num(balance?.days_taken);
  const entitlement = available + pending + used;
  const utilization = entitlement > 0 ? Math.round((used / entitlement) * 100) : 0;
  return { available, pending, used, entitlement, utilization };
}

const balanceTone = (available) => {
  if (available < 0) return 'error';
  if (available <= LOW_THRESHOLD) return 'warning';
  return 'success';
};

function groupByUser(balances) {
  const map = {};
  (balances || []).forEach((item) => {
    const key = item.user_id;
    if (!map[key]) {
      map[key] = { userId: key, user: item.user, balances: [] };
    }
    map[key].balances.push(item);
  });

  return Object.values(map).map((group) => {
    const annual = group.balances.find((b) => b.leave_type === 'Annual') || null;
    const annualBd = annual ? computeBreakdown(annual) : null;
    const hasPending = group.balances.some((b) => num(b.on_hold) > 0);
    const totalAvailable = group.balances.reduce((sum, b) => sum + num(b.balance), 0);
    const totalUsed = group.balances.reduce((sum, b) => sum + num(b.days_taken), 0);
    // "Orphan" balance: no matching employee record in the directory (the user
    // relation joins vacation_balances.user_id -> users.employee_code).
    const hasProfile = Boolean(group.user && group.user.name);
    return { ...group, annual, annualBd, hasPending, totalAvailable, totalUsed, hasProfile };
  });
}

function csvCell(value) {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportBalancesCsv(rows) {
  const header = [
    'User ID',
    'Name',
    'Email',
    'Department',
    'Manager',
    'Leave Type',
    'Entitlement',
    'Used',
    'Pending',
    'Available',
    'Utilization %',
    'Last Updated',
  ];
  const lines = [header.map(csvCell).join(',')];
  rows.forEach((b) => {
    const bd = computeBreakdown(b);
    lines.push(
      [
        b.user_id,
        b.user?.name || '',
        b.user?.email || '',
        deptOf(b.user),
        managerOf(b.user),
        b.leave_type || '',
        bd.entitlement,
        bd.used,
        bd.pending,
        bd.available,
        bd.utilization,
        b.updated_at || '',
      ]
        .map(csvCell)
        .join(',')
    );
  });

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `vacation-balances-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// -------------------------------------------------------------------------
// Small presentational pieces
// -------------------------------------------------------------------------
function BalanceBar({ bd, height = 8 }) {
  const theme = useTheme();
  if (!bd || bd.entitlement <= 0) {
    return (
      <Box
        sx={{
          height,
          borderRadius: 999,
          bgcolor: alpha(theme.palette.text.primary, 0.08),
        }}
      />
    );
  }
  const total = bd.entitlement;
  const usedPct = Math.max(0, Math.min(100, (bd.used / total) * 100));
  const pendingPct = Math.max(0, Math.min(100 - usedPct, (bd.pending / total) * 100));

  return (
    <Tooltip
      title={`Used ${formatDays(bd.used)} • Pending ${formatDays(bd.pending)} • Available ${formatDays(
        bd.available
      )} of ${formatDays(bd.entitlement)}`}
    >
      <Box
        sx={{
          height,
          borderRadius: 999,
          bgcolor: alpha(theme.palette.success.main, 0.18),
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        <Box sx={{ width: `${usedPct}%`, bgcolor: 'primary.main' }} />
        <Box sx={{ width: `${pendingPct}%`, bgcolor: 'warning.main' }} />
      </Box>
    </Tooltip>
  );
}

function AnnualCell({ annualBd }) {
  if (!annualBd) {
    return (
      <Typography variant="caption" color="text.secondary">
        No annual balance
      </Typography>
    );
  }
  const tone = balanceTone(annualBd.available);
  return (
    <Box sx={{ minWidth: 150 }}>
      <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mb: 0.4 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 850 }} color={`${tone}.main`}>
          {formatDays(annualBd.available)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          / {formatDays(annualBd.entitlement)}
        </Typography>
      </Stack>
      <BalanceBar bd={annualBd} />
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.3 }}>
        {annualBd.utilization}% used
      </Typography>
    </Box>
  );
}

function TypeBreakdownTable({ balances, navigate }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        mx: 2,
        mb: 1.5,
        borderRadius: theme.hr?.radius?.md || 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.035),
        overflow: 'hidden',
      }}
    >
      <Table size="small" aria-label="Vacation type balances">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="right">Entitlement</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="right">Used</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="right">Pending</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="right">Available</TableCell>
            <TableCell sx={{ fontWeight: 800, minWidth: 130 }}>Usage</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Last updated</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="right">Edit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {balances.map((balance) => {
            const bd = computeBreakdown(balance);
            const tone = balanceTone(bd.available);
            return (
              <TableRow key={balance.id}>
                <TableCell sx={{ fontWeight: 700 }}>{balance.leave_type || '-'}</TableCell>
                <TableCell align="right">{formatDays(bd.entitlement)}</TableCell>
                <TableCell align="right">{formatDays(bd.used)}</TableCell>
                <TableCell align="right">
                  {bd.pending > 0 ? (
                    <Typography variant="body2" color="warning.main" sx={{ fontWeight: 700 }}>
                      {formatDays(bd.pending)}
                    </Typography>
                  ) : (
                    formatDays(0)
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={`${tone}.main`} sx={{ fontWeight: 800 }}>
                    {formatDays(bd.available)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <BalanceBar bd={bd} height={6} />
                  <Typography variant="caption" color="text.secondary">
                    {bd.utilization}% used
                  </Typography>
                </TableCell>
                <TableCell>{formatDateTime(balance.updated_at)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit this balance">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/admin/vacation-balances/edit/${balance.id}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// -------------------------------------------------------------------------
// Mobile card
// -------------------------------------------------------------------------
function UserBalanceCard({ userGroup, navigate, onDeleteClick, onMissingAnnual }) {
  const theme = useTheme();
  const { userId, user, balances, annual, annualBd, hasPending, hasProfile } = userGroup;
  const [open, setOpen] = useState(false);

  const handleEditClick = () => {
    if (!annual) {
      onMissingAnnual();
      return;
    }
    navigate(`/admin/vacation-balances/edit/${annual.id}`);
  };

  const handleDelete = () => {
    const target = annual || balances[0];
    if (target) onDeleteClick(target);
  };

  return (
    <Box
      sx={{
        borderRadius: theme.hr?.radius?.md || 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: theme.hr?.surface?.glass || 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            sx={{
              width: 38,
              height: 38,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
              fontWeight: 800,
            }}
          >
            {getInitials(user?.name)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 850 }}
                noWrap
                color={hasProfile ? 'text.primary' : 'text.secondary'}
              >
                {hasProfile ? user.name : 'Unknown employee'}
              </Typography>
              {!hasProfile ? <WarningAmberRoundedIcon sx={{ fontSize: 15, color: 'warning.main' }} /> : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {hasProfile ? deptOf(user) || user?.email || `User ID: ${userId}` : `#${userId} · not in directory`}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpen((value) => !value)} aria-label="Toggle balance details">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Stack>

        {annualBd ? (
          <Box sx={{ mt: 1.25 }}>
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 850 }} color={`${balanceTone(annualBd.available)}.main`}>
                {formatDays(annualBd.available)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                available of {formatDays(annualBd.entitlement)} annual
              </Typography>
            </Stack>
            <Box sx={{ mt: 0.6 }}>
              <BalanceBar bd={annualBd} />
            </Box>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
            No annual balance configured
          </Typography>
        )}

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
          {annualBd ? (
            <Chip
              size="small"
              icon={<TrendingUpRoundedIcon sx={{ fontSize: 16 }} />}
              label={`Used: ${formatDays(annualBd.used)}`}
              variant="outlined"
            />
          ) : null}
          {hasPending ? (
            <Chip
              size="small"
              color="warning"
              icon={<HourglassBottomIcon sx={{ fontSize: 16 }} />}
              label="Pending"
            />
          ) : null}
          <Chip size="small" label={`${balances.length} leave type${balances.length === 1 ? '' : 's'}`} />
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ mt: 1.4 }}>
          <Button size="small" startIcon={<EditIcon />} onClick={handleEditClick}>
            Edit
          </Button>
          <Button size="small" startIcon={<HistoryIcon />} onClick={() => navigate(`/admin/vacation-balances/history?user_id=${userId}`)}>
            History
          </Button>
          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
            Delete
          </Button>
        </Stack>
      </Box>

      {open ? (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 1.5,
            py: 1,
            backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.035),
          }}
        >
          <Stack spacing={1}>
            {balances.map((balance) => {
              const bd = computeBreakdown(balance);
              return (
                <Box key={balance.id}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                      {balance.leave_type || '-'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800 }} color={`${balanceTone(bd.available)}.main`}>
                      {formatDays(bd.available)} avail
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 0.4 }}>
                    <BalanceBar bd={bd} height={6} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Used {formatDays(bd.used)} • Pending {formatDays(bd.pending)} • of {formatDays(bd.entitlement)}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

// -------------------------------------------------------------------------
// Desktop row
// -------------------------------------------------------------------------
function UserRow({ userGroup, navigate, onDeleteClick, onMissingAnnual }) {
  const { userId, user, balances, annual, annualBd, hasPending, hasProfile } = userGroup;
  const [open, setOpen] = useState(false);

  const handleEditClick = () => {
    if (!annual) {
      onMissingAnnual();
      return;
    }
    navigate(`/admin/vacation-balances/edit/${annual.id}`);
  };

  const handleDelete = () => {
    const target = annual || balances[0];
    if (target) onDeleteClick(target);
  };

  const usedDays = annualBd ? annualBd.used : 0;
  const pendingDays = annualBd ? annualBd.pending : 0;

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 54 }}>
          <IconButton size="small" onClick={() => setOpen((value) => !value)} aria-label="Toggle balance details">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
                fontWeight: 800,
              }}
            >
              {getInitials(user?.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 800 }}
                  noWrap
                  color={hasProfile ? 'text.primary' : 'text.secondary'}
                >
                  {hasProfile ? user.name : 'Unknown employee'}
                </Typography>
                {!hasProfile ? (
                  <Tooltip title="No matching employee record in the directory">
                    <WarningAmberRoundedIcon sx={{ fontSize: 15, color: 'warning.main' }} />
                  </Tooltip>
                ) : null}
              </Stack>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {hasProfile
                  ? deptOf(user)
                    ? `${deptOf(user)} · #${userId}`
                    : user?.email || `#${userId}`
                  : `#${userId} · not in directory`}
              </Typography>
            </Box>
          </Stack>
        </TableCell>
        <TableCell>
          <AnnualCell annualBd={annualBd} />
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {annualBd ? formatDays(usedDays) : '-'}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {pendingDays > 0 ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<HourglassBottomIcon sx={{ fontSize: 15 }} />}
              label={formatDays(pendingDays)}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip size="small" label={`${balances.length} type${balances.length === 1 ? '' : 's'}`} />
          {hasPending ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="pending"
              sx={{ ml: 0.5 }}
            />
          ) : null}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Tooltip title="Edit Annual">
            <span>
              <IconButton color="primary" onClick={handleEditClick} size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="View History">
            <span>
              <IconButton
                color="info"
                onClick={() => navigate(`/admin/vacation-balances/history?user_id=${userId}`)}
                size="small"
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <span>
              <IconButton color="error" onClick={handleDelete} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>

      {open ? (
        <TableRow>
          <TableCell colSpan={7} sx={{ p: 0 }}>
            <TypeBreakdownTable balances={balances} navigate={navigate} />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

// -------------------------------------------------------------------------
// Main page
// -------------------------------------------------------------------------
function VacationBalanceList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Deep-link from "Add user": ?user_id=<employee_code> pre-filters to that hire
  // so the admin sees all of their seeded balances at once.
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('user_id') || '');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('All');
  const [attentionFilter, setAttentionFilter] = useState('all'); // all | low | negative | pending
  const [deptFilter, setDeptFilter] = useState('All');
  const [managerFilter, setManagerFilter] = useState('All');
  const [sortBy, setSortBy] = useState('name'); // name | available_asc | available_desc | utilization_desc

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState(null);
  const [okMsg, setOkMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const fetchBalances = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const response = await api.get('/admin/vacation-balances');
      setBalances(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch vacation balances:', err);
      setLoadError('Failed to load vacation balances.');
      setErrorMsg('Failed to load vacation balances.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalances();
  }, []);

  // Distinct option lists for the filters.
  const leaveTypeOptions = useMemo(() => {
    const set = new Set();
    balances.forEach((b) => b.leave_type && set.add(b.leave_type));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [balances]);

  const deptOptions = useMemo(() => {
    const set = new Set();
    balances.forEach((b) => {
      const d = deptOf(b.user);
      if (d) set.add(d);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [balances]);

  const managerOptions = useMemo(() => {
    const set = new Set();
    balances.forEach((b) => {
      const m = managerOf(b.user);
      if (m) set.add(m);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [balances]);

  const allGroups = useMemo(() => groupByUser(balances), [balances]);
  const unmatchedCount = useMemo(() => allGroups.filter((g) => !g.hasProfile).length, [allGroups]);

  const filteredGroups = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const out = allGroups.filter((g) => {
      if (term) {
        const haystack = `${g.user?.name || ''} ${g.user?.email || ''} ${g.userId || ''} ${g.balances
          .map((b) => b.leave_type)
          .join(' ')}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (deptFilter !== 'All' && deptOf(g.user) !== deptFilter) return false;
      if (managerFilter !== 'All' && managerOf(g.user) !== managerFilter) return false;
      if (leaveTypeFilter !== 'All' && !g.balances.some((b) => b.leave_type === leaveTypeFilter)) return false;

      if (attentionFilter === 'low' && !(g.annualBd && g.annualBd.available <= LOW_THRESHOLD)) return false;
      if (attentionFilter === 'negative' && !(g.annualBd && g.annualBd.available < 0)) return false;
      if (attentionFilter === 'pending' && !g.hasPending) return false;
      if (attentionFilter === 'unmatched' && g.hasProfile) return false;

      return true;
    });

    const sorted = [...out];
    sorted.sort((a, b) => {
      // Always keep employees with a real directory profile above orphaned
      // balance records (which have no name) regardless of the sort key.
      if (a.hasProfile !== b.hasProfile) return a.hasProfile ? -1 : 1;
      if (sortBy === 'available_asc') return (a.annualBd?.available ?? Infinity) - (b.annualBd?.available ?? Infinity);
      if (sortBy === 'available_desc') return (b.annualBd?.available ?? -Infinity) - (a.annualBd?.available ?? -Infinity);
      if (sortBy === 'utilization_desc') return (b.annualBd?.utilization ?? 0) - (a.annualBd?.utilization ?? 0);
      return String(a.user?.name || '').localeCompare(String(b.user?.name || ''));
    });
    return sorted;
  }, [allGroups, searchTerm, deptFilter, managerFilter, leaveTypeFilter, attentionFilter, sortBy]);

  // Reset to first page whenever the result set is re-scoped.
  useEffect(() => {
    setPage(0);
  }, [searchTerm, deptFilter, managerFilter, leaveTypeFilter, attentionFilter, sortBy]);

  // Keep page within range after the list shrinks.
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredGroups.length / rowsPerPage) - 1);
    setPage((p) => (p > maxPage ? maxPage : p));
  }, [filteredGroups.length, rowsPerPage]);

  const paginatedGroups = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredGroups.slice(start, start + rowsPerPage);
  }, [filteredGroups, page, rowsPerPage]);

  // KPIs reflect the current (filtered) view so the cards always agree with the
  // table; with no filters active they equal the org-wide totals.
  const stats = useMemo(() => {
    let annualAvailable = 0;
    let annualUsed = 0;
    let needsAttention = 0;
    filteredGroups.forEach((g) => {
      if (g.annualBd) {
        annualAvailable += g.annualBd.available;
        annualUsed += g.annualBd.used;
        if (g.annualBd.available <= LOW_THRESHOLD) needsAttention += 1;
      }
    });
    return {
      userCount: filteredGroups.length,
      totalCount: allGroups.length,
      annualAvailable,
      annualUsed,
      needsAttention,
    };
  }, [filteredGroups, allGroups.length]);

  const handleDeleteClick = (balance) => {
    setSelectedBalance(balance);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedBalance) return;
    try {
      await api.delete(`/admin/vacation-balances/${selectedBalance.id}`);
      setOkMsg(`Deleted "${selectedBalance.leave_type}" balance successfully.`);
      await fetchBalances();
    } catch (err) {
      console.error('Failed to delete balance:', err);
      setErrorMsg('Failed to delete balance.');
    } finally {
      setConfirmOpen(false);
      setSelectedBalance(null);
    }
  };

  const hasActiveFilters =
    Boolean(searchTerm) ||
    leaveTypeFilter !== 'All' ||
    attentionFilter !== 'all' ||
    deptFilter !== 'All' ||
    managerFilter !== 'All';

  const resetFilters = () => {
    setSearchTerm('');
    setLeaveTypeFilter('All');
    setAttentionFilter('all');
    setDeptFilter('All');
    setManagerFilter('All');
  };

  const handleExport = () => {
    const idSet = new Set(filteredGroups.map((g) => g.userId));
    const rows = balances.filter((b) => {
      if (!idSet.has(b.user_id)) return false;
      if (leaveTypeFilter !== 'All' && b.leave_type !== leaveTypeFilter) return false;
      return true;
    });
    if (!rows.length) {
      setErrorMsg('Nothing to export for the current filters.');
      return;
    }
    exportBalancesCsv(rows);
    setOkMsg(`Exported ${rows.length} balance row${rows.length === 1 ? '' : 's'} to CSV.`);
  };

  const showMissingAnnual = () => setErrorMsg('No Annual balance found for this user. Only Annual can be edited.');

  const desktopTable = (
    <TableContainer>
      <Table stickyHeader size="small" aria-label="Vacation balances table">
        <TableHead>
          <TableRow>
            <TableCell width="54" />
            <TableCell>Employee</TableCell>
            <TableCell>Annual balance</TableCell>
            <TableCell align="right">Used</TableCell>
            <TableCell align="right">Pending</TableCell>
            <TableCell>Leave types</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedGroups.map((userGroup) => (
            <UserRow
              key={userGroup.userId}
              userGroup={userGroup}
              navigate={navigate}
              onDeleteClick={handleDeleteClick}
              onMissingAnnual={showMissingAnnual}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const mobileCards = (
    <Stack spacing={1.1}>
      {paginatedGroups.map((userGroup) => (
        <UserBalanceCard
          key={userGroup.userId}
          userGroup={userGroup}
          navigate={navigate}
          onDeleteClick={handleDeleteClick}
          onMissingAnnual={showMissingAnnual}
        />
      ))}
    </Stack>
  );

  return (
    <PageLayout maxWidth="xl" backgroundAccent>
      <PageHeader
        variant="dashboard"
        overline="Vacation balances"
        title="Employee leave balances"
        subtitle="Track entitlement, usage, pending holds and available days per employee — and keep every change auditable."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExport}
              disabled={loading || !filteredGroups.length}
            >
              Export CSV
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchBalances} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/vacation-balances/new')}>
              Add New
            </Button>
          </>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gap: 1.4,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
          mb: 1.6,
        }}
      >
        <StatCard
          tone="dashboard"
          icon={<PeopleAltOutlinedIcon fontSize="small" />}
          title="Employees"
          value={stats.userCount}
          hint={stats.userCount === stats.totalCount ? 'All employees' : `of ${stats.totalCount} total`}
          color="primary.main"
        />
        <StatCard
          tone="dashboard"
          icon={<CalendarMonthIcon fontSize="small" />}
          title="Annual available"
          value={formatDays(stats.annualAvailable)}
          hint="Remaining annual days"
          color="success.main"
        />
        <StatCard
          tone="dashboard"
          icon={<WorkHistoryRoundedIcon fontSize="small" />}
          title="Annual used"
          value={formatDays(stats.annualUsed)}
          hint="Consumed annual days"
          color="info.main"
        />
        <StatCard
          tone="dashboard"
          icon={<WarningAmberRoundedIcon fontSize="small" />}
          title="Needs attention"
          value={stats.needsAttention}
          hint={`Annual ≤ ${LOW_THRESHOLD} days — click to filter`}
          color="warning.main"
          onClick={() => setAttentionFilter((prev) => (prev === 'low' ? 'all' : 'low'))}
        />
      </Box>

      <FilterBar
        dense
        sx={{ mb: 1.4 }}
        onReset={hasActiveFilters ? resetFilters : undefined}
        left={
          <>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by name, ID, email, or leave type"
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
            <TextField
              select
              size="small"
              label="Leave type"
              value={leaveTypeFilter}
              onChange={(e) => setLeaveTypeFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="All">All types</MenuItem>
              {leaveTypeOptions.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Show"
              value={attentionFilter}
              onChange={(e) => setAttentionFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="low">{`Annual ≤ ${LOW_THRESHOLD} days`}</MenuItem>
              <MenuItem value="negative">Negative annual</MenuItem>
              <MenuItem value="pending">Has pending</MenuItem>
              {unmatchedCount > 0 ? (
                <MenuItem value="unmatched">{`Unmatched (${unmatchedCount})`}</MenuItem>
              ) : null}
            </TextField>
          </>
        }
        right={
          <>
            <TextField
              select
              size="small"
              label="Team"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="All">All teams</MenuItem>
              {deptOptions.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Manager"
              value={managerFilter}
              onChange={(e) => setManagerFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="All">All managers</MenuItem>
              {managerOptions.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', md: 'block' } }} />
            <TextField
              select
              size="small"
              label="Sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="name">Name (A–Z)</MenuItem>
              <MenuItem value="available_asc">Available (low → high)</MenuItem>
              <MenuItem value="available_desc">Available (high → low)</MenuItem>
              <MenuItem value="utilization_desc">Utilization (high → low)</MenuItem>
            </TextField>
          </>
        }
      />

      <DataTableShell
        title="Balance directory"
        subtitle="Bar shows used (filled) and pending (amber) against total entitlement. Expand a row for every leave type."
        loading={loading}
        error={loadError}
        onRetry={fetchBalances}
        empty={
          !filteredGroups.length ? (
            <EmptyState
              compact
              icon={HistoryIcon}
              title="No balances found"
              description={
                hasActiveFilters
                  ? 'No employees match the current filters. Try clearing them.'
                  : 'Add a new balance record to get started.'
              }
              actionLabel={hasActiveFilters ? 'Clear filters' : 'Add New'}
              onAction={hasActiveFilters ? resetFilters : () => navigate('/admin/vacation-balances/new')}
            />
          ) : null
        }
        mobileCards={mobileCards}
        tone="dashboard"
      >
        {desktopTable}
      </DataTableShell>

      {filteredGroups.length > 0 ? (
        <TablePagination
          component="div"
          count={filteredGroups.length}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10) || 10);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Employees per page"
        />
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        title="Delete vacation balance"
        description={
          selectedBalance
            ? `Delete the "${selectedBalance.leave_type}" balance for ${selectedBalance.user?.name || 'this employee'}? This action cannot be undone.`
            : 'Delete this balance? This action cannot be undone.'
        }
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setConfirmOpen(false);
          setSelectedBalance(null);
        }}
      />

      <Snackbar
        open={Boolean(okMsg)}
        autoHideDuration={3000}
        onClose={() => setOkMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setOkMsg('')} variant="filled">
          {okMsg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={4000}
        onClose={() => setErrorMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg('')} variant="filled">
          {errorMsg}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}

export default VacationBalanceList;
